import {
  CommunicationChannel,
  Language,
  QuestionType,
  type Session,
} from '../models/session';
import { MessageSenderType } from '../models/message';

const AUTHORITY_FIELDS = [
  'aiPaused',
  'adminIntervention',
  'currentQuestionId',
  'currentQuestionType',
  'stateVersion',
  'takeoverEpoch',
] as const satisfies ReadonlyArray<keyof Session>;

const AUTHORITY_VALUE_FIELDS = [
  'aiPaused',
  'adminIntervention',
  'currentQuestionId',
  'currentQuestionType',
] as const satisfies ReadonlyArray<keyof Session>;

const DELIVERY_FIELDS = [
  'lastMessage',
  'lastMessageSender',
  'lastMessageAt',
  'lastInboundAt',
  'adminLastViewedAt',
] as const satisfies ReadonlyArray<keyof Session>;

const LAST_MESSAGE_FIELDS = [
  'lastMessage',
  'lastMessageSender',
  'lastMessageAt',
] as const satisfies ReadonlyArray<keyof Session>;

const VERSIONLESS_SOCKET_FIELDS = new Set<keyof Session>([
  'id',
  'workerId',
  ...DELIVERY_FIELDS,
]);

const KNOWN_SESSION_FIELDS = new Set<keyof Session>([
  'id',
  'workerId',
  'publicShareId',
  'phoneNumber',
  'workerFirstName',
  'workerLastName',
  'currentQuestionId',
  'language',
  'professionKey',
  'channel',
  'currentQuestionType',
  'answers',
  'aiPaused',
  'adminIntervention',
  'stateVersion',
  'takeoverEpoch',
  'createdAt',
  'updatedAt',
  'lastMessage',
  'lastMessageSender',
  'lastMessageAt',
  'lastInboundAt',
  'adminLastViewedAt',
  'verificationAttempts',
  'verificationStartedAt',
  'aiBotStatus',
  'temporaryAnswerData',
  'temporaryAiValidationFeedback',
  'isActive',
  'referralLinkId',
  'attributionConfidence',
  'ctwaClid',
  'metaAdId',
  'metaCampaignId',
]);

const DECIMAL_BIGINT = /^(0|[1-9][0-9]{0,18})$/;
const MAX_SIGNED_BIGINT = 9_223_372_036_854_775_807n;

export interface SessionAuthorityVersion {
  stateVersion: string;
  takeoverEpoch: string;
}

export type SessionAuthorityDecision = 'NONE' | 'APPLY' | 'REJECT';

const hasOwn = (value: object, key: PropertyKey): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

/**
 * SQL list projections represent a session without messages as an explicit
 * all-null tuple. The HTTP contract uses optional delivery fields, so remove
 * only that exact empty tuple before applying the strict snapshot validator.
 */
export const normalizeSessionHttpSnapshot = (value: unknown): unknown => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return value;
  const candidate = value as Record<string, unknown>;
  const hasCompleteTuple = LAST_MESSAGE_FIELDS.every((key) => hasOwn(candidate, key));
  const hasEmptyTuple = LAST_MESSAGE_FIELDS.every((key) => candidate[key] === null);
  if (!hasCompleteTuple || !hasEmptyTuple) return value;

  const normalized = { ...candidate };
  LAST_MESSAGE_FIELDS.forEach((key) => delete normalized[key]);
  return normalized;
};

const parseVersion = (value: unknown): bigint | null => {
  if (typeof value !== 'string' || !DECIMAL_BIGINT.test(value)) return null;
  const parsed = BigInt(value);
  return parsed <= MAX_SIGNED_BIGINT ? parsed : null;
};

export const readSessionAuthorityVersion = (
  value: Pick<Partial<Session>, 'stateVersion' | 'takeoverEpoch'>,
): SessionAuthorityVersion | null => {
  if (
    parseVersion(value.stateVersion) === null ||
    parseVersion(value.takeoverEpoch) === null
  ) {
    return null;
  }
  return {
    stateVersion: value.stateVersion as string,
    takeoverEpoch: value.takeoverEpoch as string,
  };
};

export const hasSessionAuthorityFields = (value: Partial<Session>): boolean =>
  AUTHORITY_FIELDS.some((key) => hasOwn(value, key));

const hasOnlyKnownFields = (value: Partial<Session>): boolean =>
  Object.keys(value).every((key) => KNOWN_SESSION_FIELDS.has(key as keyof Session));

const isVersionlessDeliveryUpdate = (value: Partial<Session>): boolean =>
  Object.keys(value).every((key) => VERSIONLESS_SOCKET_FIELDS.has(key as keyof Session));

const hasValidSuppliedAuthorityValues = (value: Partial<Session>): boolean =>
  (!hasOwn(value, 'aiPaused') || typeof value.aiPaused === 'boolean') &&
  (!hasOwn(value, 'adminIntervention') || typeof value.adminIntervention === 'boolean') &&
  (!hasOwn(value, 'currentQuestionId') ||
    value.currentQuestionId === null ||
    (Number.isSafeInteger(value.currentQuestionId) && Number(value.currentQuestionId) > 0)) &&
  (!hasOwn(value, 'currentQuestionType') ||
    value.currentQuestionType === null ||
    Object.values(QuestionType).includes(value.currentQuestionType as QuestionType));

const hasCoherentLastMessageTuple = (value: Partial<Session>): boolean => {
  const supplied = LAST_MESSAGE_FIELDS.filter((key) => hasOwn(value, key));
  return supplied.length === 0 || supplied.length === LAST_MESSAGE_FIELDS.length;
};

const hasValidSuppliedDeliveryValues = (value: Partial<Session>): boolean =>
  hasCoherentLastMessageTuple(value) &&
  (!hasOwn(value, 'lastMessage') || typeof value.lastMessage === 'string') &&
  (!hasOwn(value, 'lastMessageSender') ||
    Object.values(MessageSenderType).includes(value.lastMessageSender as MessageSenderType)) &&
  (!hasOwn(value, 'lastMessageAt') ||
    (typeof value.lastMessageAt === 'string' && isTimestamp(value.lastMessageAt))) &&
  (!hasOwn(value, 'lastInboundAt') ||
    value.lastInboundAt === null ||
    (typeof value.lastInboundAt === 'string' && isTimestamp(value.lastInboundAt))) &&
  (!hasOwn(value, 'adminLastViewedAt') ||
    value.adminLastViewedAt === null ||
    (typeof value.adminLastViewedAt === 'string' && isTimestamp(value.adminLastViewedAt)));

const isTimestamp = (value: string): boolean => Number.isFinite(Date.parse(value));
const isOptionalString = (value: unknown): boolean =>
  value === undefined || typeof value === 'string';
const isOptionalNullableString = (value: unknown): boolean =>
  value === undefined || value === null || typeof value === 'string';
const isOptionalTimestamp = (value: unknown, nullable = false): boolean =>
  value === undefined ||
  (nullable && value === null) ||
  (typeof value === 'string' && isTimestamp(value));

export const hasValidSessionAuthoritySnapshot = (
  value: Partial<Session>,
): value is Partial<Session> & Pick<Session, 'id' | 'workerId' | 'aiPaused' | 'adminIntervention' | 'stateVersion' | 'takeoverEpoch'> =>
  Number.isSafeInteger(value.id) &&
  Number(value.id) > 0 &&
  typeof value.workerId === 'string' &&
  value.workerId.trim().length > 0 &&
  typeof value.aiPaused === 'boolean' &&
  typeof value.adminIntervention === 'boolean' &&
  readSessionAuthorityVersion(value) !== null;

export const isValidSessionHttpSnapshot = (value: unknown): value is Session => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const candidate = value as Partial<Session>;
  return hasValidSessionAuthoritySnapshot(candidate) &&
    typeof candidate.publicShareId === 'string' && candidate.publicShareId.trim().length > 0 &&
    isOptionalNullableString(candidate.phoneNumber) &&
    isOptionalNullableString(candidate.workerFirstName) &&
    isOptionalNullableString(candidate.workerLastName) &&
    Object.values(Language).includes(candidate.language as Language) &&
    isOptionalNullableString(candidate.professionKey) &&
    Object.values(CommunicationChannel).includes(candidate.channel as CommunicationChannel) &&
    (candidate.answers === undefined ||
      (typeof candidate.answers === 'object' && candidate.answers !== null &&
        !Array.isArray(candidate.answers))) &&
    (candidate.currentQuestionId === undefined || candidate.currentQuestionId === null ||
      (Number.isSafeInteger(candidate.currentQuestionId) && candidate.currentQuestionId > 0)) &&
    (candidate.currentQuestionType === undefined || candidate.currentQuestionType === null ||
      Object.values(QuestionType).includes(candidate.currentQuestionType)) &&
    typeof candidate.createdAt === 'string' && isTimestamp(candidate.createdAt) &&
    typeof candidate.updatedAt === 'string' && isTimestamp(candidate.updatedAt) &&
    hasCoherentLastMessageTuple(candidate) &&
    isOptionalString(candidate.lastMessage) &&
    (candidate.lastMessageSender === undefined ||
      Object.values(MessageSenderType).includes(candidate.lastMessageSender)) &&
    isOptionalTimestamp(candidate.lastMessageAt) &&
    isOptionalTimestamp(candidate.lastInboundAt, true) &&
    isOptionalTimestamp(candidate.adminLastViewedAt, true) &&
    (candidate.verificationAttempts === undefined ||
      (Number.isSafeInteger(candidate.verificationAttempts) && candidate.verificationAttempts >= 0)) &&
    isOptionalTimestamp(candidate.verificationStartedAt, true) &&
    isOptionalNullableString(candidate.aiBotStatus) &&
    (candidate.temporaryAnswerData === undefined || candidate.temporaryAnswerData === null ||
      (typeof candidate.temporaryAnswerData === 'object' &&
        !Array.isArray(candidate.temporaryAnswerData))) &&
    isOptionalNullableString(candidate.temporaryAiValidationFeedback) &&
    (candidate.isActive === undefined || typeof candidate.isActive === 'boolean') &&
    isOptionalNullableString(candidate.referralLinkId) &&
    isOptionalNullableString(candidate.attributionConfidence) &&
    isOptionalNullableString(candidate.ctwaClid) &&
    isOptionalNullableString(candidate.metaAdId) &&
    isOptionalNullableString(candidate.metaCampaignId);
};

export const matchesSessionIdentity = (
  current: Pick<Session, 'id' | 'workerId'>,
  incoming: Partial<Pick<Session, 'id' | 'workerId'>>,
): boolean => {
  const hasIdentity = incoming.id !== undefined || incoming.workerId !== undefined;
  return hasIdentity &&
    (incoming.id === undefined || incoming.id === current.id) &&
    (incoming.workerId === undefined || incoming.workerId === current.workerId);
};

export const decideSessionAuthorityUpdate = (
  current: Partial<Session>,
  incoming: Partial<Session>,
): SessionAuthorityDecision => {
  if (!hasOnlyKnownFields(incoming)) return 'REJECT';
  if (
    !hasValidSuppliedAuthorityValues(incoming) ||
    !hasValidSuppliedDeliveryValues(incoming)
  ) return 'REJECT';
  if (!hasSessionAuthorityFields(incoming)) {
    return isVersionlessDeliveryUpdate(incoming) ? 'NONE' : 'REJECT';
  }
  const currentVersion = readSessionAuthorityVersion(current);
  const incomingVersion = readSessionAuthorityVersion(incoming);
  if (!currentVersion || !incomingVersion) return 'REJECT';

  const currentEpoch = BigInt(currentVersion.takeoverEpoch);
  const incomingEpoch = BigInt(incomingVersion.takeoverEpoch);
  if (incomingEpoch !== currentEpoch) {
    return incomingEpoch > currentEpoch ? 'APPLY' : 'REJECT';
  }
  const currentStateVersion = BigInt(currentVersion.stateVersion);
  const incomingStateVersion = BigInt(incomingVersion.stateVersion);
  if (incomingStateVersion > currentStateVersion) return 'APPLY';
  if (incomingStateVersion < currentStateVersion) return 'REJECT';

  return AUTHORITY_VALUE_FIELDS.every((key) =>
    !hasOwn(incoming, key) || Object.is(incoming[key], current[key]))
    ? 'APPLY'
    : 'REJECT';
};

const isStrictlyNewerTimestamp = (
  incoming: string | null | undefined,
  current: string | null | undefined,
): boolean => {
  if (!incoming) return !current;
  if (!current) return true;
  return Date.parse(incoming) > Date.parse(current);
};

interface DeliveryMergeResult {
  session: Session;
  requiresReconciliation?: true;
}

const mergeDeliveryFields = (
  current: Session,
  incoming: Partial<Session>,
): DeliveryMergeResult => {
  const merged = { ...current };
  let requiresReconciliation = false;

  if (LAST_MESSAGE_FIELDS.every((key) => hasOwn(incoming, key))) {
    const incomingTimestamp = Date.parse(incoming.lastMessageAt as string);
    const currentTimestamp = current.lastMessageAt
      ? Date.parse(current.lastMessageAt)
      : Number.NEGATIVE_INFINITY;
    const sameTuple = incomingTimestamp === currentTimestamp &&
      incoming.lastMessage === current.lastMessage &&
      incoming.lastMessageSender === current.lastMessageSender;

    if (incomingTimestamp > currentTimestamp || sameTuple) {
      merged.lastMessage = incoming.lastMessage;
      merged.lastMessageSender = incoming.lastMessageSender;
      merged.lastMessageAt = incoming.lastMessageAt;
    } else if (incomingTimestamp === currentTimestamp) {
      requiresReconciliation = true;
    }
  }

  (['lastInboundAt', 'adminLastViewedAt'] as const).forEach((key) => {
    if (
      hasOwn(incoming, key) &&
      isStrictlyNewerTimestamp(incoming[key], current[key])
    ) {
      Object.assign(merged, { [key]: incoming[key] });
    }
  });
  return {
    session: merged,
    ...(requiresReconciliation ? { requiresReconciliation: true as const } : {}),
  };
};

const hasEqualAuthorityVersion = (
  current: Partial<Session>,
  incoming: Partial<Session>,
): boolean => {
  const currentVersion = readSessionAuthorityVersion(current);
  const incomingVersion = readSessionAuthorityVersion(incoming);
  return Boolean(
    currentVersion &&
    incomingVersion &&
    currentVersion.stateVersion === incomingVersion.stateVersion &&
    currentVersion.takeoverEpoch === incomingVersion.takeoverEpoch,
  );
};

export const mergeSessionSocketUpdate = (
  current: Session,
  incoming: Partial<Session>,
): {
  session: Session;
  authorityDecision: SessionAuthorityDecision;
  requiresReconciliation?: true;
} => {
  if (!matchesSessionIdentity(current, incoming)) {
    return { session: current, authorityDecision: 'REJECT' };
  }
  const authorityDecision = decideSessionAuthorityUpdate(current, incoming);
  if (authorityDecision === 'REJECT') {
    return { session: current, authorityDecision };
  }

  if (
    authorityDecision === 'NONE' ||
    hasEqualAuthorityVersion(current, incoming)
  ) {
    const deliveryMerge = mergeDeliveryFields(current, incoming);
    return {
      authorityDecision,
      ...deliveryMerge,
    };
  }

  const mergedWithoutDelivery = {
    ...current,
    ...incoming,
    id: current.id,
    workerId: current.workerId,
    publicShareId: incoming.publicShareId ?? current.publicShareId,
    phoneNumber:
      hasOwn(incoming, 'phoneNumber') && incoming.phoneNumber !== undefined
        ? incoming.phoneNumber
        : current.phoneNumber,
  };
  const deliveryMerge = mergeDeliveryFields(current, incoming);
  const merged = {
    ...mergedWithoutDelivery,
    ...Object.fromEntries(DELIVERY_FIELDS.map((key) => [key, deliveryMerge.session[key]])),
  };
  if (!isValidSessionHttpSnapshot(merged)) {
    return { session: current, authorityDecision: 'REJECT' };
  }
  return {
    authorityDecision,
    session: merged,
    ...(deliveryMerge.requiresReconciliation
      ? { requiresReconciliation: true as const }
      : {}),
  };
};

export const isNewerSessionReplacementSnapshot = (
  current: Session,
  incoming: Partial<Session>,
): incoming is Session => {
  if (
    incoming.workerId !== current.workerId ||
    incoming.id === current.id ||
    !isValidSessionHttpSnapshot(incoming)
  ) {
    return false;
  }

  return incoming.id > current.id &&
    Date.parse(incoming.createdAt) > Date.parse(current.createdAt);
};

export const mergeSessionListSocketUpdate = (
  current: Session,
  incoming: Partial<Session>,
): {
  session: Session;
  authorityDecision: SessionAuthorityDecision;
  replacement: boolean;
  requiresReconciliation?: true;
} => {
  if (isNewerSessionReplacementSnapshot(current, incoming)) {
    return {
      session: { ...incoming },
      authorityDecision: 'APPLY',
      replacement: true,
    };
  }

  return {
    ...mergeSessionSocketUpdate(current, incoming),
    replacement: false,
  };
};

export interface SessionSocketReplayUpdate {
  payload: Partial<Session>;
  allowCreate: boolean;
}

export const replaySessionSocketUpdates = (
  snapshots: Session[],
  updates: readonly SessionSocketReplayUpdate[],
): Session[] => updates.reduce<Session[]>((current, update) => {
  const index = current.findIndex((session) =>
    (update.payload.id !== undefined && session.id === update.payload.id) ||
    (update.payload.workerId !== undefined && session.workerId === update.payload.workerId));
  if (index >= 0) {
    const next = [...current];
    next[index] = mergeSessionListSocketUpdate(
      current[index],
      update.payload,
    ).session;
    return next;
  }
  if (update.allowCreate && isValidSessionHttpSnapshot(update.payload)) {
    return [...current, { ...update.payload }];
  }
  return current;
}, [...snapshots]);
